/* Mission Control: brand satellites around the sunrise (Home hero).
   Built on the Sponsors orbit engine's rules: logo sizes are measured, the inner ring clears the core,
   rings and per-ring capacity are fitted to the stage, and brands that don't fit wait in a queue and take
   over a slot as it passes behind the sun. Two refinements for the hero:
   - Satellites travel by arc length (not by angle), so spacing stays even on a flattened, inclined orbit.
   - Every candidate layout is verified numerically across a full revolution (every slot pair within a
     ring, every relative phase between rings, the stage edges, and the sun's keep-out) before it is used,
     so logos can never overlap each other, the sun, or leave the stage.
   Retro layer: a satellite emerging from behind the sun arrives as a coarse pixel mosaic of its own mark
   and resolves to crisp in steps as it swings into the light; it dissolves the same way going behind.
   Sparse hard-edged pixel stars trail in the orbit's wake.
   Reduced motion (and the theme editor) get a still, composed frame: crisp logos, rings, no motion. */
(() => {
  const reducedQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  const TWO_PI = Math.PI * 2;
  const BACK = Math.PI * 1.5; // far apex (top of the ellipse), where a satellite passes behind the sun
  const TILT = (-7 * Math.PI) / 180;
  const MAXS = 1.1; // near-side scale
  const MINS = 0.64; // far-side scale
  const GAP = 12;
  const PX_BLOCKS = [0, 3, 5, 8]; // css px per mosaic block: crisp, fine, medium, coarse

  const scaleAt = (th) => MINS + (MAXS - MINS) * ((Math.sin(th) + 1) / 2);
  const wrap = (a) => ((a % TWO_PI) + TWO_PI) % TWO_PI;
  const angDist = (a, b) => { const d = Math.abs(wrap(a) - wrap(b)); return d > Math.PI ? TWO_PI - d : d; };

  // Cumulative arc length of an ellipse, sampled by parametric angle, so arc position maps to angle
  function arcTable(rx, ry, n = 720) {
    const t = new Float64Array(n + 1);
    let acc = 0, px = rx, py = 0;
    for (let i = 1; i <= n; i++) {
      const th = (i / n) * TWO_PI;
      const x = rx * Math.cos(th), y = ry * Math.sin(th);
      acc += Math.hypot(x - px, y - py);
      t[i] = acc;
      px = x; py = y;
    }
    return { t, P: acc, n };
  }
  function thetaAt(tab, s) {
    s = ((s % tab.P) + tab.P) % tab.P;
    let lo = 0, hi = tab.n;
    while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (tab.t[mid] <= s) lo = mid; else hi = mid; }
    const span = tab.t[hi] - tab.t[lo] || 1;
    return ((lo + (s - tab.t[lo]) / span) / tab.n) * TWO_PI;
  }

  function Satellites(root) {
    const stage = root.querySelector('[data-mc-sat-stage]');
    const wake = root.querySelector('[data-mc-sat-wake]');
    const pauseBtn = root.querySelector('[data-mc-sat-pause]');
    const hero = root.closest('[data-mc-hero]');
    const els = [...stage.querySelectorAll('.mc-hero__sat')];
    if (els.length < 3 || !wake) return;
    const ctx = wake.getContext('2d');
    const tmp = document.createElement('canvas');
    const tctx = tmp.getContext('2d');
    const still = () => reducedQ.matches || document.documentElement.classList.contains('shopify-design-mode');

    const all = els.map((el, i) => {
      const img = el.querySelector('img.mc-logo');
      let cv = null;
      if (img) {
        cv = document.createElement('canvas');
        cv.className = 'mc-hero__px';
        el.querySelector('.mc-hero__sat-box').appendChild(cv);
      }
      return { el, img, cv, i, w: 0, h: 0, level: 0 };
    });

    let W = 0, H = 0, cx = 0, cy = 0, dpr = 1, coreR = 0;
    let rings = [], slots = [], queue = [];
    let clock = 0, last = 0, raf = 0, speed = 1, intro = 0;
    let hovering = false, pinned = false, visible = true, armed = false;
    let sparks = [], sparkTimer = 0;

    function pointAt(g, th) {
      const lx = g.rx * Math.cos(th), ly = g.ry * Math.sin(th);
      const c = Math.cos(TILT), s = Math.sin(TILT);
      return { x: cx + lx * c - ly * s, y: cy + lx * s + ly * c, z: Math.sin(th), k: scaleAt(th) };
    }
    const boxOf = (g, th, bw, bh) => { const p = pointAt(g, th); return { x: p.x, y: p.y, z: p.z, w: bw * p.k, h: bh * p.k }; };
    const hit = (a, b) => Math.abs(a.x - b.x) * 2 < a.w + b.w + GAP && Math.abs(a.y - b.y) * 2 < a.h + b.h + GAP;
    const inStage = (b) => b.x - b.w / 2 >= 2 && b.x + b.w / 2 <= W - 2 && b.y - b.h / 2 >= 2 && b.y + b.h / 2 <= H - 2;
    // A satellite in front of the sun must clear its bright disc; behind it, the corona covers it
    const clearsCore = (b) => {
      if (b.z < -0.15) return true;
      const dx = Math.max(Math.abs(b.x - cx) - b.w / 2, 0), dy = Math.max(Math.abs(b.y - cy) - b.h / 2, 0);
      return Math.hypot(dx, dy) >= coreR;
    };

    // Numeric verification of one ring holding n evenly spaced slots, across a whole revolution
    function ringOk(g, n, bw, bh) {
      const step = g.tab.P / n;
      for (let k = 0; k < 36; k++) {
        const off = (k / 36) * step;
        const boxes = [];
        for (let j = 0; j < n; j++) {
          const b = boxOf(g, thetaAt(g.tab, off + j * step), bw, bh);
          if (!inStage(b) || !clearsCore(b)) return false;
          for (const o of boxes) if (hit(o, b)) return false;
          boxes.push(b);
        }
      }
      return true;
    }
    // Two rings turning in opposite directions meet at every relative phase: check them all
    function pairOk(a, b, bw, bh) {
      const sa = a.tab.P / a.n, sb = b.tab.P / b.n;
      const A = [], B = [];
      for (let k = 0; k < 20; k++) {
        const ra = [], rb = [];
        for (let j = 0; j < a.n; j++) ra.push(boxOf(a, thetaAt(a.tab, (k / 20) * sa + j * sa), bw, bh));
        for (let j = 0; j < b.n; j++) rb.push(boxOf(b, thetaAt(b.tab, (k / 20) * sb + j * sb), bw, bh));
        A.push(ra); B.push(rb);
      }
      for (const ra of A) for (const rb of B) for (const x of ra) for (const y of rb) if (hit(x, y)) return false;
      return true;
    }
    // Fit one ring: keep its width, open its height in steps (flattest first) until a layout verifies
    function fitRing(rx, ry, ryMax, bw, bh, want) {
      let best = null;
      for (let h = ry; h <= ryMax + 0.5; h += 6) {
        const g = { rx, ry: Math.min(h, ryMax), tab: arcTable(rx, Math.min(h, ryMax)) };
        let n = Math.floor(g.tab.P / (bw * MAXS + GAP * 1.5));
        while (n >= 3 && !ringOk(g, n, bw, bh)) n--;
        if (n >= 3 && (!best || n > best.n)) { g.n = n; best = g; }
        if (best && best.n >= want) break;
      }
      return best;
    }
    function fill(candidates, bw, bh, ryMax) {
      const out = [];
      for (const c of candidates) {
        const g = fitRing(c.rx, c.ry, Math.min(ryMax, c.rx * 0.72), bw, bh, W < 600 ? 5 : 9);
        if (!g) return null;
        out.push(g);
      }
      if (out.length === 2) {
        // Trim the busier ring until the pair clears at every relative phase
        let guard = 12;
        while (!pairOk(out[0], out[1], bw, bh)) {
          const busy = out[0].n >= out[1].n ? out[0] : out[1];
          if (busy.n <= 3 || !guard--) return null;
          busy.n--;
        }
      }
      return out;
    }

    function measure() {
      // Layout size, not getBoundingClientRect(): the orbit layer is scaled by the hero's scroll story
      W = stage.offsetWidth; H = stage.offsetHeight;
      if (W < 40 || H < 40) return false;
      cx = W / 2; cy = H / 2;
      dpr = Math.min(2, window.devicePixelRatio || 1);
      wake.width = Math.round(W * dpr);
      wake.height = Math.round(H * dpr);
      all.forEach((b) => { b.w = b.el.offsetWidth; b.h = b.el.offsetHeight; });
      const bw = Math.max(...all.map((b) => b.w));
      const bh = Math.max(...all.map((b) => b.h));
      const coreW = Math.max(72, Math.min(W, H) * 0.36);
      stage.style.setProperty('--core', `${coreW.toFixed(0)}px`);
      coreR = coreW * 0.2;

      const flat = W < 600 ? 0.4 : 0.5; // the orbit is seen at an inclination
      const outRx = W / 2 - (bw * MAXS) / 2 - 6;
      const outRy = H / 2 - (bh * MAXS) / 2 - 6;
      const inRx = coreR + (bw * MAXS) / 2 + GAP;
      const inRy = coreR + (bh * MAXS) / 2 + GAP;
      const ringAt = (rx) => ({ rx, ry: Math.max(Math.min(outRy, rx * flat), Math.min(inRy, outRy)) });

      let plan = null;
      // Two rings when the stage is roomy: the outer at full reach, the inner as small as still verifies
      if (outRx > inRx + bw * 1.05) {
        for (let f = 0.4; f <= 0.74 && !plan; f += 0.04) {
          const inner = ringAt(Math.max(inRx, outRx * f));
          if (inner.rx > outRx - bw) break;
          plan = fill([inner, ringAt(outRx)], bw, bh, outRy);
        }
      }
      if (!plan) plan = fill([ringAt(Math.max(inRx, outRx))], bw, bh, outRy);
      if (!plan) { rings = []; slots = []; return false; }

      // Arc speed from a revolution time per ring; neighbouring rings turn opposite ways
      const periods = plan.length === 2 ? [52, 76] : [64];
      rings = plan.map((g, i) => ({ ...g, speed: g.tab.P / periods[i], dir: i % 2 ? -1 : 1 }));

      // Slots ride the rings; brands (in roster order) occupy slots, the rest queue
      slots = [];
      let bi = 0;
      rings.forEach((g, ri) => {
        const step = g.tab.P / g.n;
        const phase = ri * 0.37 * step + g.tab.P * 0.06;
        for (let j = 0; j < g.n; j++) {
          slots.push({ ring: ri, s0: phase + j * step, brand: all[bi] || null, prev: null, order: bi });
          bi++;
        }
      });
      slots = slots.filter((s) => s.brand);
      queue = all.slice(slots.length);
      all.forEach((b) => b.el.classList.toggle('is-queued', queue.includes(b)));
      queue.forEach((b) => { b.el.style.opacity = '0'; });
      return true;
    }

    function setLevel(b, level) {
      if (!b.cv || b.level === level) return;
      if (level === 0) { b.el.classList.remove('is-px'); b.level = 0; return; }
      const img = b.img;
      const iw = img.offsetWidth, ih = img.offsetHeight;
      if (!iw || !ih || !img.complete || !img.naturalWidth) return;
      const cw = Math.round(iw * dpr), ch = Math.round(ih * dpr);
      if (b.cv.width !== cw || b.cv.height !== ch) {
        b.cv.width = cw; b.cv.height = ch;
        const cs = getComputedStyle(img);
        b.cv.style.filter = cs.filter;
        b.cv.style.opacity = cs.opacity;
      }
      const block = PX_BLOCKS[level];
      const sw = Math.max(2, Math.round(iw / block)), sh = Math.max(2, Math.round(ih / block));
      tmp.width = sw; tmp.height = sh;
      tctx.imageSmoothingEnabled = true;
      tctx.clearRect(0, 0, sw, sh);
      tctx.drawImage(img, 0, 0, sw, sh);
      const c = b.cv.getContext('2d');
      c.imageSmoothingEnabled = false;
      c.clearRect(0, 0, cw, ch);
      c.drawImage(tmp, 0, 0, sw, sh, 0, 0, cw, ch);
      b.level = level;
      b.el.classList.add('is-px');
    }

    function place() {
      const calm = still();
      slots.forEach((slot) => {
        const g = rings[slot.ring];
        const th = thetaAt(g.tab, slot.s0 + clock * g.speed * g.dir);
        // Hand the slot to the next queued brand as it passes behind the sun
        if (!calm && queue.length && slot.prev !== null) {
          const crossed = g.dir > 0 ? slot.prev < BACK && th >= BACK && th - slot.prev < 1 : slot.prev > BACK && th <= BACK && slot.prev - th < 1;
          if (crossed) {
            const out = slot.brand;
            out.el.classList.add('is-queued');
            out.el.style.opacity = '0';
            setLevel(out, 0);
            slot.brand = queue.shift();
            slot.brand.el.classList.remove('is-queued');
            queue.push(out);
          }
        }
        slot.prev = th;
        const b = slot.brand;
        const p = pointAt(g, th);
        const depth = (p.z + 1) / 2;
        const back = angDist(th, BACK);
        b.el.style.transform = `translate3d(${(p.x - b.w / 2).toFixed(1)}px, ${(p.y - b.h / 2).toFixed(1)}px, 0) scale(${p.k.toFixed(3)})`;
        // Depth fade, a dissolve right behind the sun, and the staggered arrival on load
        let o = 0.42 + 0.58 * depth;
        if (back < 0.4) o *= back / 0.4;
        o *= Math.min(1, Math.max(0, intro * 1.8 - slot.order * 0.07));
        b.el.style.opacity = o.toFixed(3);
        b.el.style.zIndex = p.z > 0 ? String(100 + Math.round(depth * 100)) : String(Math.round(depth * 40));
        // Signal acquisition: coarse pixels near the sun, crisp in the light
        setLevel(b, calm ? 0 : back < 0.55 ? 3 : back < 0.75 ? 2 : back < 0.95 ? 1 : 0);
      });
    }

    function spawnSpark() {
      const live = slots.filter((s) => s.brand && !s.brand.el.classList.contains('is-queued'));
      if (!live.length || sparks.length > 18) return;
      const slot = live[(Math.random() * live.length) | 0];
      const g = rings[slot.ring];
      const trail = slot.s0 + clock * g.speed * g.dir - g.dir * (slot.brand.w * 0.55 + 10);
      const th = thetaAt(g.tab, trail);
      if (angDist(th, BACK) < 0.9) return;
      const p = pointAt(g, th);
      const nx = p.x - cx, ny = p.y - cy, len = Math.hypot(nx, ny) || 1;
      sparks.push({ x: p.x, y: p.y, vx: (nx / len) * 5, vy: (ny / len) * 5, t: 0, life: 1.4 + Math.random() * 0.8, warm: Math.random() < 0.25 });
    }

    function drawWake(dt, ringAlpha) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      rings.forEach((g, i) => {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(TILT);
        ctx.beginPath();
        ctx.ellipse(0, 0, g.rx, g.ry, 0, 0, TWO_PI);
        const outer = i === rings.length - 1 && rings.length > 1;
        ctx.strokeStyle = outer ? 'rgba(138, 160, 205, 0.18)' : 'rgba(138, 160, 205, 0.3)';
        ctx.setLineDash(outer ? [2, 6] : []);
        ctx.lineWidth = 1;
        ctx.globalAlpha = ringAlpha;
        ctx.stroke();
        ctx.restore();
      });
      // Pixel stars: a 5-block cross on a whole-pixel grid, fading as it drifts off the ring
      const u = 2;
      sparks = sparks.filter((s) => (s.t += dt) < s.life);
      sparks.forEach((s) => {
        const a = Math.sin((s.t / s.life) * Math.PI);
        const x = Math.round(s.x + s.vx * s.t), y = Math.round(s.y + s.vy * s.t);
        const rgb = s.warm ? '255, 214, 160' : '190, 228, 255';
        ctx.fillStyle = `rgba(${rgb}, ${(a * 0.9).toFixed(3)})`;
        ctx.fillRect(x, y, u, u);
        ctx.fillStyle = `rgba(${rgb}, ${(a * 0.45).toFixed(3)})`;
        ctx.fillRect(x - u, y, u, u); ctx.fillRect(x + u, y, u, u); ctx.fillRect(x, y - u, u, u); ctx.fillRect(x, y + u, u, u);
      });
    }

    const progress = () => (hero ? parseFloat(hero.style.getPropertyValue('--p')) || 0 : 0);

    function frame(now) {
      raf = 0;
      if (still()) { render(); return; }
      if (!armed || !visible || document.hidden || progress() > 0.9) return;
      const dt = Math.min(0.05, (now - (last || now)) / 1000);
      last = now;
      const want = hovering || pinned ? 0 : 1;
      speed += (want - speed) * Math.min(1, dt * 4);
      if (want === 0 && speed < 0.002) speed = 0;
      clock += dt * speed;
      intro = Math.min(1, intro + dt / 1.6);
      place();
      sparkTimer -= dt * speed;
      if (sparkTimer <= 0) { spawnSpark(); sparkTimer = 0.22; }
      drawWake(dt, Math.min(1, intro * 1.5));
      // Keep running while the orbit should be moving (or easing, fading sparks, arriving)
      if (want > 0 || speed > 0 || sparks.length || intro < 1) raf = requestAnimationFrame(frame);
    }
    const kick = () => { if (!raf && armed && !still()) { last = 0; raf = requestAnimationFrame(frame); } };

    function render() {
      if (!measure()) return;
      if (still()) {
        intro = 1;
        clock = 0;
        all.forEach((b) => setLevel(b, 0));
        place();
        drawWake(0, 1);
      } else {
        place();
        drawWake(0, Math.min(1, intro * 1.5));
        kick();
      }
    }

    function arm() {
      armed = true;
      stage.classList.add('is-armed');
      if (!still() && pauseBtn) pauseBtn.hidden = false;
      render();
    }

    stage.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { hovering = true; } });
    stage.addEventListener('pointerleave', () => { hovering = false; kick(); });
    pauseBtn?.addEventListener('click', () => {
      pinned = !pinned;
      pauseBtn.setAttribute('aria-pressed', String(pinned));
      const label = pauseBtn.querySelector('[data-label-on]');
      if (label) label.textContent = pinned ? label.dataset.labelOff : label.dataset.labelOn;
      kick();
    });

    let pending = 0;
    const rerender = () => { if (pending) return; pending = requestAnimationFrame(() => { pending = 0; if (armed) render(); }); };
    new ResizeObserver(rerender).observe(stage);
    all.forEach((b) => b.img?.addEventListener('load', rerender));
    document.fonts?.ready.then(rerender);
    new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; kick(); }, { rootMargin: '60px' }).observe(stage);
    document.addEventListener('visibilitychange', kick);
    window.addEventListener('scroll', () => {
      if (!visible) return;
      if (pauseBtn) { const ti = progress() > 0.5 ? -1 : 0; if (pauseBtn.tabIndex !== ti) pauseBtn.tabIndex = ti; }
      kick();
    }, { passive: true });
    reducedQ.addEventListener?.('change', () => { if (pauseBtn) pauseBtn.hidden = still(); render(); });

    // Arm as the sun crests the horizon; a still frame arms at once
    if (still()) arm(); else setTimeout(arm, 1100);
  }

  function init() {
    document.querySelectorAll('[data-mc-sat-root]').forEach((el) => { if (!el.__mc) { el.__mc = 1; Satellites(el); } });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
